import Component from "../../lib/component";
import {popoverWindow} from "./popover_window";
import {hasChildElement} from "../../helpers/element";
import {XHRFetcher} from "../../lib/xhr_fetcher";

import iconQuestion from './assets/icon__question.svg';
import iconMagnify from './assets/icon__magnify.svg';
import iconDownChevron from './assets/icon__down_chevron.svg';
import iconInfo from './assets/icon__info.svg';

export const PopoverConfig = {
    componentTypeKey: 'Popover',
    baseHtmlClass: 'popover',
    selector: '.popover',

    initializedHtmlClass: 'popover--inited',
    iconHtmlClass: 'popover__icon',
    notInitializedSelector: '.popover:not(.popover--inited)',
    openModifier: 'open',
    emptyModifier: 'empty',
    localModifier: 'local',
    remoteModifier: 'remote',
    loadingModifier: 'loading',
    types: ['peak', 'menu', 'errors', 'help', 'modal', 'warn', 'info'],

    peakIcon: iconMagnify,
    menuIcon: iconDownChevron,
    errorsIcon: iconMagnify,
    helpIcon: iconQuestion,
    modalIcon: iconMagnify,
    warnIcon: iconMagnify,
    infoIcon: iconInfo,
}

export class Popover extends Component {
    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: PopoverConfig,
        });

        this.setType();
        this.popoverWindow = new popoverWindow(this);
        this.setSourceType();
        if (this.hasLocalSource() && this.hasEmptyLocalSource()) {
            this.addState(this.config.emptyModifier);
        } else {
            this.removeState(this.config.emptyModifier);
        }
        this.listen();

        this.renderIcon();
    }

    // Idempotent: the link may already hold the icon. A component that copies
    // markup as a string (the crew Attributor re-renders its value from
    // innerHTML) brings the icon with it into a fresh, un-initialised element,
    // which is then initialised again; appending regardless doubled the icon.
    renderIcon() {
        if (!this.type) return;
        if (this.element.querySelector(`:scope > .${this.config.iconHtmlClass}`)) return;
        const svgStr = this.config[`${this.type}Icon`];
        if (svgStr) {
            const svgElement = new DOMParser().parseFromString(
                svgStr,
                "image/svg+xml",
            ).documentElement;
            svgElement.classList.add(this.config.iconHtmlClass);
            this.element.appendChild(svgElement);
        }
    }

    // Examine the element for a state class like 'popover--help' and then set the type property based on this.
    setType() {
        this.type = null;
        this.config.types.forEach((type) => {
            if (this.hasState(type)) {
                this.type = type;
            } else if (this.element.getAttribute("data-popover-type")) {
                this.type = this.element.getAttribute("data-popover-type");
            }
        });
    }

    // The type can come from the data attribute `data-popover-type` or extracted from the class like `popover--help`.
    getType() {
        let type = this.element.getAttribute("data-popover-type");
        if (!type) {
            this.config.types.forEach((config_type) => {
                if (this.hasState(config_type)) {
                    type = config_type;
                }
            });
        }
        return type;
    }

    getBoxWidth() {
        return this.element.getAttribute("data-popover-width");
    }

    getBoxZIndex() {
        return this.element.getAttribute("data-popover-zindex");
    }

    listen() {
        this.element.addEventListener("click", (event) => {
            this.isOpen() ? this.doClose(event) : this.doOpen(event);
        });
    }

    // Handle opening and loading content…

    doOpen(event) {
        this.addState(this.config.openModifier);
        this.openWindow(event);
        this.loadWindowContent();
        this.fireOpenCallback();
        event.preventDefault();
        event.stopPropagation();
    }

    openWindow(event) {
        this.popoverWindow.open(0, 0);
    }

    loadWindowContent() {
        const source = this.getLocalSourceId();
        if (source == null) {
            const url = this.getRemoteSourceHref();
            this.addState(this.config.loadingModifier);
            this.popoverWindow.addLoadingState();
            // Preserve the old Loader behaviour: on failure, set null content and stop loading.
            const handleContent = (response) => {
                this.setWindowContent(response);
                this.stopLoading();
            };
            this.loader = new XHRFetcher(url, { headers: { Accept: "text/javascript" } });
            this.loader.fetch()
                .then(handleContent)
                .catch(() => handleContent(null));
        } else {
            let sourceElement = this.getLocalSourceElement();
            if (sourceElement != null) {
                this.setWindowContent(sourceElement.innerHTML);
            }
            this.stopLoading();
        }
    }

    setWindowContent(content) {
        this.popoverWindow.setContent(content);
    }

    stopLoading() {
        this.removeState(this.config.loadingModifier);
        this.popoverWindow.removeLoadingState();
        this.popoverWindow.render();
    }

    // Handle closing…

    doClose(event) {
        this.closeWindow();
        event.preventDefault();
        event.stopPropagation();
    }

    closeWindow() {
        this.removeState(this.config.openModifier);
        this.popoverWindow.close();
    }

    // Handle callback

    fireOpenCallback() {
        const callback = this.element.getAttribute("data-popover-callback");
        if (callback == null) return;
        try {
            new Function(callback)();
        } catch (e) {}
    }

    // Source

    setSourceType() {
        this.sourceType = this.getSourceType();
        this.addState(this.getSourceType());
    }

    getSourceType() {
        if (this.getLocalSourceId() !== null) {
            return this.config.localModifier;
        }
        if (this.getRemoteSourceHref() !== null) {
            return this.config.remoteModifier;
        }

        return null;
    }

    getLocalSourceId() {
        return this.element.getAttribute("data-popover-source");
    }

    getRemoteSourceHref() {
        return this.element.getAttribute("href");
    }

    hasLocalSource() {
        return this.sourceType === this.config.localModifier;
    }

    hasRemoteSource() {
        return this.sourceType === this.config.remoteModifier;
    }

    hasEmptyLocalSource() {
        const sourceElement = this.getLocalSourceElement();
        if (sourceElement == null) return true;
        return !hasChildElement(sourceElement);
    }

    getLocalSourceElement() {
        return document.getElementById(this.getLocalSourceId());
    }

    // Private helpers…

    isOpen() {
        return this.hasState(this.config.openModifier);
    }

    hasState(state) {
        return this.element.classList.contains(
            `${this.config.baseHtmlClass}--${state}`,
        );
    }

    addState(state) {
        if (state === null) {
            return;
        }
        this.element.classList.add(`${this.config.baseHtmlClass}--${state}`);
    }

    removeState(state) {
        if (state === null) {
            return;
        }
        this.element.classList.remove(`${this.config.baseHtmlClass}--${state}`);
    }
}

